import { useEffect, useState } from 'react'
import {
    MessagesSquare,
    CalendarClock,
    MessageCircle,
    ThumbsUp,
} from 'lucide-react'
import { getFeedbackSummary } from '~/api/feedback'
import { listSessions } from '~/api/sessions'
import { getTimeseries } from '~/api/stats'
import { useStats } from '~/api/use-stats'
import type {
    AdminSession,
    FeedbackSummary,
    TimeseriesPoint,
} from '~/api/types'
import { ChartCard, SeriesArea, KRDS } from '~/components/charts'
import { FeedbackPanel } from '~/components/FeedbackPanel'
import { LogTable } from '~/components/LogTable'
import { ServicePageShell } from '~/components/ServicePageShell'
import { StatCard } from '~/components/StatCard'
import { TranscriptPanel } from '~/components/TranscriptPanel'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Card } from '~/components/ui/card'
import { EmptyState } from '~/components/ui/empty-state'
import { SearchInput } from '~/components/ui/search-input'
import { Skeleton } from '~/components/ui/skeleton'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import { useToast } from '~/components/ui/toast'
import { cn, formatDateTime, whoLabel } from '~/lib/utils'

// 상담 서비스 페이지. 개요(KPI·추이·최근 세션·피드백 요약) + 대화로그 + 피드백 탭.
const TREND_DAYS = 30
const RECENT_LIMIT = 5

const STATUS_TONES: Record<string, BadgeTone> = {
    active: 'info',
    completed: 'success',
    archived: 'neutral',
}

export function ConsultationPage() {
    const toast = useToast()
    const { stats, error } = useStats()
    const c = stats?.consultation

    // 개요에서 세션 행 클릭 시 대화로그 탭(인덱스 1)으로 이동하며 해당 세션을 선택한다.
    const [active, setActive] = useState(0)
    const [selected, setSelected] = useState<AdminSession | null>(null)

    const [recent, setRecent] = useState<AdminSession[] | null>(null)
    const [series, setSeries] = useState<TimeseriesPoint[]>([])
    const [feedback, setFeedback] = useState<FeedbackSummary | null>(null)
    const [q, setQ] = useState('')

    // 통계 조회 오류는 토스트로 알린다.
    useEffect(() => {
        if (error) toast.error(error)
    }, [error, toast])

    // 추이·피드백 요약은 검색과 무관해 마운트 시 1회만 조회한다.
    useEffect(() => {
        let alive = true
        getTimeseries(TREND_DAYS)
            .then((d) => alive && setSeries(d.series))
            .catch(() => {})
        getFeedbackSummary('consultation')
            .then((d) => alive && setFeedback(d))
            .catch(() => {})
        return () => {
            alive = false
        }
    }, [])

    // 최근 세션은 검색어(제목·사용자 ILIKE)가 바뀌면 다시 조회한다.
    useEffect(() => {
        let alive = true
        listSessions({
            profile: 'consultation',
            q: q.trim() || undefined,
            limit: RECENT_LIMIT,
        })
            .then((d) => alive && setRecent(d))
            .catch(() => alive && setRecent([]))
        return () => {
            alive = false
        }
    }, [q])

    const upRate =
        feedback && feedback.total > 0
            ? `${Math.round((feedback.up / feedback.total) * 100)}%`
            : '-'

    const trendEmpty = series.every((p) => p.consultations === 0)
    // 암묵적 만족 프록시(자발적 👍/👎의 부정 편향 보정): 계속 물어본(멀티턴) 비율과
    // 공식창구로 안 넘어가고 해결된(비에스컬레이션) 비율.
    const sessions = c?.sessions ?? 0
    const engagementRate =
        c && sessions > 0
            ? Math.round(((c.multiTurnSessions ?? 0) / sessions) * 100)
            : null
    const resolutionRate =
        c && sessions > 0
            ? Math.round((1 - c.consultations / sessions) * 100)
            : null

    function openInLog(session: AdminSession) {
        setSelected(session)
        setActive(1)
    }

    const overview = (
        <div className='space-y-6'>
            <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
                <StatCard
                    label='세션'
                    value={c?.sessions ?? '-'}
                    icon={MessagesSquare}
                    tone='primary'
                />
                <StatCard
                    label='최근 7일'
                    value={c?.sessions7d ?? '-'}
                    hint='세션 생성'
                    icon={CalendarClock}
                    tone='info'
                />
                <StatCard
                    label='상담'
                    value={c?.consultations ?? '-'}
                    icon={MessageCircle}
                />
                <StatCard
                    label='👍 만족도'
                    value={upRate}
                    hint={`피드백 ${feedback?.total ?? '-'}건`}
                    icon={ThumbsUp}
                    tone='success'
                />
            </div>

            <ChartCard
                title='일별 상담 추이'
                description={`최근 ${TREND_DAYS}일 상담 건수`}
                empty={trendEmpty}
                emptyText='기간 내 상담 데이터가 없어요.'>
                <SeriesArea
                    data={series}
                    dataKey='consultations'
                    name='상담'
                    color={KRDS.primary}
                />
            </ChartCard>

            <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
                <Card className='lg:col-span-2'>
                    <div className='flex items-start justify-between gap-3 p-5 pb-3'>
                        <div>
                            <h3 className='krds-title text-foreground'>
                                최근 세션
                            </h3>
                            <p className='text-muted-foreground mt-0.5 text-sm'>
                                행을 클릭하면 대화로그에서 재생해요.
                            </p>
                        </div>
                        <SearchInput
                            className='w-60'
                            value={q}
                            onChange={setQ}
                            placeholder='제목·사용자 검색'
                        />
                    </div>
                    <div className='px-5 pb-5'>
                        {recent !== null && recent.length === 0 ? (
                            <EmptyState
                                icon={MessagesSquare}
                                title='세션이 없어요.'
                                description='상담 세션이 생성되면 여기에 표시돼요.'
                            />
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>식별자</TableHead>
                                        <TableHead>사용자</TableHead>
                                        <TableHead>상태</TableHead>
                                        <TableHead>프로필</TableHead>
                                        <TableHead>생성일</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {recent === null
                                        ? Array.from({
                                              length: RECENT_LIMIT,
                                          }).map((_, i) => (
                                              <TableRow key={i}>
                                                  <TableCell>
                                                      <Skeleton className='h-4 w-28' />
                                                  </TableCell>
                                                  <TableCell>
                                                      <Skeleton className='h-4 w-28' />
                                                  </TableCell>
                                                  <TableCell>
                                                      <Skeleton className='h-4 w-16' />
                                                  </TableCell>
                                                  <TableCell>
                                                      <Skeleton className='h-4 w-20' />
                                                  </TableCell>
                                                  <TableCell>
                                                      <Skeleton className='h-4 w-28' />
                                                  </TableCell>
                                              </TableRow>
                                          ))
                                        : recent.map((s) => (
                                              <TableRow
                                                  key={s.id}
                                                  onClick={() => openInLog(s)}
                                                  className={cn(
                                                      'cursor-pointer',
                                                      s.id === selected?.id &&
                                                          'bg-accent hover:bg-accent'
                                                  )}>
                                                  <TableCell
                                                      className='max-w-[200px] truncate'
                                                      title={s.title ?? s.id}>
                                                      {s.title ? (
                                                          s.title
                                                      ) : (
                                                          <span className='font-mono text-xs'>
                                                              {s.id.slice(0, 8)}
                                                          </span>
                                                      )}
                                                  </TableCell>
                                                  <TableCell
                                                      className='max-w-[180px] truncate'
                                                      title={
                                                          s.userEmail ??
                                                          undefined
                                                      }>
                                                      {whoLabel(
                                                          s.userName,
                                                          s.userEmail
                                                      )}
                                                  </TableCell>
                                                  <TableCell>
                                                      <Badge
                                                          tone={
                                                              STATUS_TONES[
                                                                  s.status
                                                              ] ?? 'neutral'
                                                          }>
                                                          {s.status}
                                                      </Badge>
                                                  </TableCell>
                                                  <TableCell>
                                                      {s.profile}
                                                  </TableCell>
                                                  <TableCell>
                                                      {formatDateTime(
                                                          s.createdAt
                                                      )}
                                                  </TableCell>
                                              </TableRow>
                                          ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                </Card>

                <Card className='p-5'>
                    <h3 className='krds-title text-foreground'>피드백 요약</h3>
                    <p className='text-muted-foreground mt-0.5 text-sm'>
                        상담 응답 만족도
                    </p>
                    <div className='mt-4 grid grid-cols-2 gap-3'>
                        <div>
                            <div className='krds-label'>👍 긍정</div>
                            <div className='text-success krds-heading-m mt-1'>
                                {feedback?.up ?? '-'}
                            </div>
                        </div>
                        <div>
                            <div className='krds-label'>👎 부정</div>
                            <div className='text-point krds-heading-m mt-1'>
                                {feedback?.down ?? '-'}
                            </div>
                        </div>
                    </div>
                    <div className='border-border mt-4 flex items-center justify-between border-t pt-4'>
                        <span className='krds-label'>명시적 긍정률 (👍)</span>
                        <span className='text-foreground text-lg font-bold tabular-nums'>
                            {upRate}
                        </span>
                    </div>

                    <div className='border-border mt-4 border-t pt-4'>
                        <p className='krds-label mb-2'>
                            암묵적 만족 (참여 기반)
                        </p>
                        <div className='space-y-2'>
                            <div className='flex items-center justify-between'>
                                <span className='text-muted-foreground text-sm'>
                                    멀티턴 참여율
                                </span>
                                <span className='text-foreground font-semibold tabular-nums'>
                                    {engagementRate != null
                                        ? `${engagementRate}%`
                                        : '-'}
                                </span>
                            </div>
                            <div className='flex items-center justify-between'>
                                <span className='text-muted-foreground text-sm'>
                                    해결율 (비에스컬레이션)
                                </span>
                                <span className='text-foreground font-semibold tabular-nums'>
                                    {resolutionRate != null
                                        ? `${resolutionRate}%`
                                        : '-'}
                                </span>
                            </div>
                        </div>
                        <p className='text-muted-foreground mt-2 text-xs leading-relaxed'>
                            자발적 평가는 불편할 때만 누르는 경향이 있어요. 계속
                            물어본 비율(멀티턴)과 공식창구로 넘어가지 않고 해결된
                            비율을 함께 보면 만족도를 더 균형 있게 볼 수 있어요.
                        </p>
                    </div>
                </Card>
            </div>
        </div>
    )

    const logs = (
        <>
            <LogTable
                profile='consultation'
                selectedId={selected?.id}
                onSelect={setSelected}
            />
            {selected ? (
                <TranscriptPanel
                    sessionId={selected.id}
                    onClose={() => setSelected(null)}
                />
            ) : null}
        </>
    )

    return (
        <ServicePageShell
            title='상담'
            description='HACCP 상담 서비스 개요'
            active={active}
            onActiveChange={setActive}
            tabs={[
                { label: '개요', content: overview },
                { label: '대화로그', content: logs },
                {
                    label: '피드백',
                    content: <FeedbackPanel service='consultation' />,
                },
            ]}
        />
    )
}
